import * as THREE from 'three';

/**
 * IntroMessage - Floating intro panel at the start of the gallery
 * Displays a message explaining the gallery experience
 */
export class IntroMessage {
  constructor(scene, startPosition, startRotation = 0) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.isVisible = true;
    this.fadeStarted = false;
    this.opacity = 1;

    this.createPanel(startPosition, startRotation);
    this.scene.add(this.group);
  }

  createPanel(startPosition, startRotation) {
    // Panel dimensions
    const panelWidth = 4;
    const panelHeight = 3;

    // Create canvas for text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 1024;
    canvas.height = 768;

    // Draw background with gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, 'rgba(10, 10, 15, 0.95)');
    gradient.addColorStop(1, 'rgba(5, 5, 10, 0.95)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw border
    ctx.strokeStyle = 'rgba(100, 100, 120, 0.5)';
    ctx.lineWidth = 4;
    ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

    // Draw inner glow line
    ctx.strokeStyle = 'rgba(150, 150, 180, 0.3)';
    ctx.lineWidth = 1;
    ctx.strokeRect(30, 30, canvas.width - 60, canvas.height - 60);

    // Title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 56px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.fillText('VOID GALLERY', canvas.width / 2, 100);

    // Subtitle line
    ctx.fillStyle = '#888888';
    ctx.font = '24px "Courier New", monospace';
    ctx.fillText('~ a maze of thoughts ~', canvas.width / 2, 140);

    // Main text - multi-line
    const lines = [
      '',
      'This is a representation of my mind.',
      'ADHD. Depression. Anxiety.',
      '',
      'It\'s chaotic, random, dark,',
      'funny, meme-y, and all over the place.',
      'Just like my brain.',
      '',
      'The maze is different every time.',
      'The order is different every time.',
      'Just like my thoughts.',
      '',
      '131 videos. Some memes. Some dark.',
    ];

    ctx.fillStyle = '#cccccc';
    ctx.font = '28px "Courier New", monospace';
    ctx.textAlign = 'center';

    let y = 200;
    const lineHeight = 40;

    for (const line of lines) {
      if (line === '') {
        y += lineHeight * 0.5;
        continue;
      }

      // Highlight certain words
      if (line.includes('ADHD') || line.includes('Depression') || line.includes('Anxiety')) {
        ctx.fillStyle = '#ff6b6b';
        ctx.fillText(line, canvas.width / 2, y);
        ctx.fillStyle = '#cccccc';
      } else if (line.includes('different every time')) {
        ctx.fillStyle = '#6bcfff';
        ctx.fillText(line, canvas.width / 2, y);
        ctx.fillStyle = '#cccccc';
      } else if (line.includes('131 videos')) {
        ctx.fillStyle = '#a0a0a0';
        ctx.font = '24px "Courier New", monospace';
        ctx.fillText(line, canvas.width / 2, y);
        ctx.font = '28px "Courier New", monospace';
        ctx.fillStyle = '#cccccc';
      } else {
        ctx.fillText(line, canvas.width / 2, y);
      }
      y += lineHeight;
    }

    // Instructions at bottom
    ctx.fillStyle = '#666666';
    ctx.font = '22px "Courier New", monospace';
    ctx.fillText('[ click to enter ]', canvas.width / 2, canvas.height - 60);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    // Create panel mesh
    const geometry = new THREE.PlaneGeometry(panelWidth, panelHeight);
    this.material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const panel = new THREE.Mesh(geometry, this.material);
    this.group.add(panel);

    // Add subtle glow effect behind the panel
    const glowGeometry = new THREE.PlaneGeometry(panelWidth + 0.3, panelHeight + 0.3);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x222233,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.position.z = -0.05;
    this.glowMaterial = glowMaterial;
    this.group.add(glow);

    // Position the panel in front of the player
    // Place it at eye level, 3 units in front of start position
    const distance = 3;
    const offsetX = Math.sin(startRotation) * distance;
    const offsetZ = -Math.cos(startRotation) * distance;

    this.group.position.set(
      startPosition.x + offsetX,
      startPosition.y + 0.3, // Slightly above eye level
      startPosition.z + offsetZ
    );

    // Rotate to face the player
    this.group.rotation.y = startRotation;
  }

  /**
   * Start fade out animation
   */
  fadeOut() {
    if (this.fadeStarted) return;
    this.fadeStarted = true;
  }

  /**
   * Update the intro message (call in animation loop)
   * Returns false when fully faded and should be removed
   */
  update(deltaTime) {
    if (!this.isVisible) return false;

    if (this.fadeStarted) {
      this.opacity -= deltaTime * 1.5; // Fade over ~0.7 seconds

      if (this.opacity <= 0) {
        this.opacity = 0;
        this.isVisible = false;
        return false;
      }

      this.material.opacity = this.opacity;
      this.glowMaterial.opacity = this.opacity * 0.5;
    }

    return true;
  }

  /**
   * Check if player has moved away from start
   */
  checkPlayerDistance(playerPosition, startPosition, threshold = 2) {
    const dx = playerPosition.x - startPosition.x;
    const dz = playerPosition.z - startPosition.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    if (distance > threshold) {
      this.fadeOut();
    }
  }

  dispose() {
    if (this.material) {
      if (this.material.map) {
        this.material.map.dispose();
      }
      this.material.dispose();
    }

    if (this.glowMaterial) {
      this.glowMaterial.dispose();
    }

    this.group.children.forEach(child => {
      if (child.geometry) {
        child.geometry.dispose();
      }
    });

    this.scene.remove(this.group);
  }
}
